/**
 * Paint a canvas one pixel a tile in a single `putImageData`. A `fillRect` a
 * pixel costs a style parse and a draw call each: over the 384×384 world it
 * froze the page more than a second at every change of zone.
 */
export function paintPixels(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  colourAt: (x: number, y: number) => string | undefined,
): void {
  const image = context.getImageData(0, 0, width, height);
  const data = image.data;
  const parsed = new Map<string, readonly [number, number, number, number]>();
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const colour = colourAt(x, y);
      if (colour === undefined) continue;
      let rgba = parsed.get(colour);
      if (rgba === undefined) {
        rgba = parseColour(colour);
        parsed.set(colour, rgba);
      }
      const at = (y * width + x) * 4;
      data[at] = rgba[0];
      data[at + 1] = rgba[1];
      data[at + 2] = rgba[2];
      data[at + 3] = rgba[3];
    }
  }
  context.putImageData(image, 0, 0);
}

/** `#rgb`, `#rrggbb` or anything else the canvas understands, as RGBA bytes. */
function parseColour(colour: string): readonly [number, number, number, number] {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(colour)?.[1];
  if (hex !== undefined) {
    const full = hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex;
    return [
      Number.parseInt(full.slice(0, 2), 16),
      Number.parseInt(full.slice(2, 4), 16),
      Number.parseInt(full.slice(4, 6), 16),
      255,
    ];
  }
  // Named or rgba() colours: let a one-pixel canvas read them once.
  const probe = document.createElement('canvas').getContext('2d');
  if (probe === null) return [0, 0, 0, 255];
  probe.fillStyle = colour;
  probe.fillRect(0, 0, 1, 1);
  const [r = 0, g = 0, b = 0, a = 255] = probe.getImageData(0, 0, 1, 1).data;
  return [r, g, b, a];
}
